import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type PagedResult,
  type ProjectFolderListItem,
  type ProjectFolderDetail,
  type ProjectFolderWrite,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { HoverTooltip } from '../lib/HoverTooltip';

interface ProjectStateOption {
  id: number;
  name: string;
}

interface ProjectCountryOption {
  id: number;
  name: string;
  states: ProjectStateOption[];
}

/**
 * Project folders list — mirrors `.old/resources/views/project_folder/index.blade.php`.
 */
export function ProjectsListPage() {
  const { user } = useAuth();
  const isSuper = !!user?.roles.includes('super-admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const params = new URLSearchParams({ pageSize: '200' });
  if (isSuper && showAll) params.set('all', 'true');

  const { data, isLoading, error } = useQuery({
    queryKey: ['project-folders', { all: showAll }],
    queryFn: () => api.get<PagedResult<ProjectFolderListItem>>(`/api/v1/project-folders?${params}`),
  });

  const del = useMutation({
    mutationFn: (id: number) => api.del(`/api/v1/project-folders/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-folders'] }),
  });

  const allItems = data?.items ?? [];
  const filtered = allItems.filter((p) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [p.name, p.city, p.state].some((v) => (v ?? '').toLowerCase().includes(term));
  });

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(page, pageCount - 1);
  const start = clampedPage * pageSize;
  const visible = filtered.slice(start, start + pageSize);
  const showingFrom = total === 0 ? 0 : start + 1;
  const showingTo = Math.min(start + pageSize, total);

  return (
    <div className="container-fluid">
      <div className="row mt-lg-3 mt-1">
        <div className="col col-xl-12 col-lg-12">
          <h1>Projects</h1>
        </div>
      </div>

      <div className="row mt-lg-1 mt-1">
        <div className="col-sm-12">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
            <label className="d-flex align-items-center gap-2 mb-0 small">
              Show
              <select
                className="form-select form-select-sm"
                style={{ width: 80 }}
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              >
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              entries
            </label>
            <div className="d-flex align-items-center gap-3">
              {isSuper && (
                <div className="form-check mb-0">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="proj-show-all"
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="proj-show-all">All users</label>
                </div>
              )}
              <label className="d-flex align-items-center gap-2 mb-0 small">
                Search:
                <input
                  type="search"
                  className="form-control form-control-sm"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                />
              </label>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table align-middle w-100">
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Status</th>
                  <th>Structure Types</th>
                  <th>Total Structures</th>
                  <th>Last Modified</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={6} className="text-center text-muted py-3">Loading…</td></tr>
                )}
                {error && (
                  <tr><td colSpan={6} className="text-center text-danger py-3">Failed to load projects.</td></tr>
                )}
                {!isLoading && !error && visible.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-3">No data available in table</td></tr>
                )}
                {visible.map((p) => (
                  <tr key={p.id}>
                    <td style={{ verticalAlign: 'middle' }}>{p.name}</td>
                    <td style={{ verticalAlign: 'middle' }}>{projectStatus(p)}</td>
                    <td style={{ verticalAlign: 'middle' }}>{p.structureCount}</td>
                    <td style={{ verticalAlign: 'middle' }}>{p.totalStructures}</td>
                    <td style={{ verticalAlign: 'middle' }}>{formatDateTime(p.updatedAt)}</td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        <Link to={`/admin/projects/${p.id}/summary`} className="btn btn-sm btn-outline-primary">
                          View Project
                        </Link>
                        <Link to={`/project/addstructure/${p.id}`} className="btn btn-sm btn-outline-primary">
                          Update/Edit Project
                        </Link>
                        <button
                          type="button"
                          className="btn p-0 btn-link ms-1 text-danger"
                          aria-label={`Delete ${p.name}`}
                          onClick={() => {
                            if (confirm(`The project "${p.name}" will be removed.`)) del.mutate(p.id);
                          }}
                        >
                          <img src="/images/ICON/Icon_Trash.png" alt="Delete" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-center mt-2 gap-2">
            <span className="text-muted small">
              Showing {showingFrom} to {showingTo} of {total} entries
            </span>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={clampedPage <= 0}
                onClick={() => setPage((v) => Math.max(0, v - 1))}
              >
                ‹
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={clampedPage >= pageCount - 1}
                onClick={() => setPage((v) => Math.min(pageCount - 1, v + 1))}
              >
                ›
              </button>
            </div>
          </div>

          <div className="col-12 text-center mt-3">
            <button type="button" className="btn btn-primary" onClick={() => navigate('/newprojectfolder')}>
              Create New Project
            </button>
            <span className="ps-2">
              <HoverTooltip
                text="Create a new project to get quotes on all the ADS parts you need at the same time."
                placement="right"
              >
                <img src="/images/ICON/Icon_Info_Green_Filled_Small.png" alt="Create project info" />
              </HoverTooltip>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function projectStatus(p: ProjectFolderListItem): string {
  const completed = p.completedStatus ? 'Complete' : 'Incomplete';
  const sent = p.deliveredStatus || !!p.shared ? 'Sent' : 'Unsent';
  return `${completed} : ${sent}`;
}

export function ProjectCreatePage() {
  const navigate = useNavigate();

  return (
    <div className="container-fluid">
      <div className="row mt-3">
        <div className="col-12 text-center">
          <h1>{legacyCreateCopy.title}</h1>
          <p className="fs-3 d-none d-lg-block">
            {legacyCreateCopy.subtitle}<br />
            {legacyCreateCopy.reportNotice}
          </p>
          <p className="fs-6 d-block d-lg-none">
            {legacyCreateCopy.subtitle}<br />
            {legacyCreateCopy.reportNotice}
          </p>
        </div>
      </div>
      <NewProjectForm
        onCreated={(p) => navigate(`/project/addstructure/${p.id}`)}
        legacyLayout
      />
      <div className="row border-top mt-4" />
      <div className="row mt-5 justify-content-center text-center">
        <div className="col-12">
          <h1 className="mt-1">Want To Learn More Before Getting Started?</h1>
          <p className="fs-3">
            Watch this short tutorial video to learn how you can get the most out of the ADS FlexStorm Project Planner.
          </p>
          <Link to="/help" className="btn btn-primary btn-lg">Watch the Video</Link>
        </div>
      </div>
    </div>
  );
}

function NewProjectForm({
  onCreated,
  legacyLayout = false,
}: {
  onCreated: (p: ProjectFolderDetail) => void;
  legacyLayout?: boolean;
}) {
  const countriesQ = useQuery({
    queryKey: ['countries'],
    queryFn: () => api.get<ProjectCountryOption[]>('/api/v1/countries'),
    staleTime: Infinity,
  });
  const [form, setForm] = useState<ProjectFolderWrite>({ name: '', city: '', state: '' });
  const [error, setError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => api.post<ProjectFolderDetail>('/api/v1/project-folders', form),
    onSuccess: onCreated,
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  });

  const set = <K extends keyof ProjectFolderWrite>(k: K, v: ProjectFolderWrite[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="row mt-2">
      <div
        className={legacyLayout ? 'col-12 mx-auto px-0' : 'col-12'}
        style={legacyLayout ? { maxWidth: 900 } : undefined}
      >
        <div className={legacyLayout ? '' : 'card'}>
          {!legacyLayout && (
          <div className="card-header">
            <h4 className="header-title mb-0">New Project</h4>
          </div>
          )}
          <div className={legacyLayout ? '' : 'card-body'}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                m.mutate();
              }}
            >
              <div className={legacyLayout ? '' : 'row g-3'}>
              <div className={legacyLayout ? 'row g-0 align-items-center mb-2' : 'col-md-4'}>
              <div className={legacyLayout ? 'col-lg-11 col-12 form-floating' : undefined}>
                <input
                  className="form-control border border-3"
                  placeholder={legacyLayout ? 'Project Name*' : undefined}
                  required
                  aria-required="true"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                />
                <label className="form-label">
                  {legacyLayout ? 'Project Name*' : <>Name <span className="text-danger">*</span></>}
                  {!legacyLayout && <>{' '}<ProjectInfoIcon text="Give your project a name so you can find it later. Location, business type, or date all make for good names" /></>}
                </label>
              </div>
              <div className="col-lg-1 d-none d-lg-block">
                {legacyLayout && <ProjectInfoIcon text="Give your project a name so you can find it later. Location, business type, or date all make for good names" />}
              </div>
              </div>
              <div className={legacyLayout ? 'row g-0 align-items-center mb-2' : 'col-md-6'}>
              <div className={legacyLayout ? 'col-lg-11 col-12 form-floating' : undefined}>
                <input
                  className="form-control border border-3"
                  placeholder={legacyLayout ? 'Design Engineer' : undefined}
                  value={form.designEngineer ?? ''}
                  onChange={(e) => set('designEngineer', e.target.value)}
                />
                <label className="form-label">
                  Design Engineer
                  {!legacyLayout && <>{' '}<ProjectInfoIcon text="Who is the design engineer on this project? If there is no engineer, leave this field blank." /></>}
                </label>
              </div>
              <div className="col-lg-1 d-none d-lg-block">
                {legacyLayout && <ProjectInfoIcon text="Who is the design engineer on this project? If there is no engineer, leave this field blank." />}
              </div>
              </div>
              <div className={legacyLayout ? 'row g-0 align-items-center mb-2' : 'col-md-6'}>
              <div className={legacyLayout ? 'col-lg-11 col-12 form-floating' : undefined}>
                <input
                  className="form-control border border-3"
                  placeholder={legacyLayout ? 'Contractor' : undefined}
                  value={form.siteContractor ?? ''}
                  onChange={(e) => set('siteContractor', e.target.value)}
                />
                <label className="form-label">
                  Contractor
                  {!legacyLayout && <>{' '}<ProjectInfoIcon text="Who is the contractor on this project? If there is no contractor, leave this field blank." /></>}
                </label>
              </div>
              <div className="col-lg-1 d-none d-lg-block">
                {legacyLayout && <ProjectInfoIcon text="Who is the contractor on this project? If there is no contractor, leave this field blank." />}
              </div>
              </div>
              <div className={legacyLayout ? 'row g-0 mb-2' : 'col-md-4'}>
              <div className={legacyLayout ? 'col-lg-11 col-12' : undefined}>
                <select
                  className={legacyLayout ? 'form-select border border-3' : 'form-select'}
                  required
                  aria-required="true"
                  value={form.state}
                  onChange={(e) => set('state', e.target.value)}
                >
                  <option value="">State/Province*</option>
                  {(countriesQ.data ?? []).flatMap((country) => country.states).map((state) => (
                    <option key={state.id} value={state.name}>{state.name}</option>
                  ))}
                </select>
                {!legacyLayout && <label className="form-label">State</label>}
              </div>
              {legacyLayout && (
                <div className="col-lg-1 d-none d-lg-block">
                  <ProjectInfoIcon text="What state is the construction project located in." />
                </div>
              )}
              </div>
              <div className={legacyLayout ? 'row g-0 mb-2' : 'col-md-6'}>
              <div className={legacyLayout ? 'col-lg-11 col-12 form-floating' : undefined}>
                <input
                  className="form-control border border-3"
                  placeholder={legacyLayout ? 'Area/City*' : undefined}
                  required
                  aria-required="true"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                />
                <label className="form-label">{legacyLayout ? 'Area/City*' : 'Area/City'}</label>
              </div>
              {legacyLayout && (
                <div className="col-lg-1 d-none d-lg-block">
                  <ProjectInfoIcon text="What area/city is the construction project located in." />
                </div>
              )}
              </div>
              {!legacyLayout && <div className="col-12">
                <label className="form-label fs-5" htmlFor="project_additional_notes">
                  Additional Requirements or Notes{' '}
                  <HoverTooltip
                    text="Add any additional information we should know (i.e. special requirements, project details, etc). 160 character maximum."
                    maxWidth={320}
                  >
                    <img
                      src="/images/ICON/Icon_Info_Green_Filled_Small.png"
                      alt="Additional requirements information"
                      title="Add any additional information we should know (i.e. special requirements, project details, etc). 160 character maximum."
                    />
                  </HoverTooltip>
                </label>
                <textarea
                  id="project_additional_notes"
                  className="form-control border border-3"
                  rows={4}
                  maxLength={160}
                  placeholder="OPTIONAL: Add any additional information we should know (i.e. special requirements, project details, etc). 160 character maximum."
                  value={form.additionalNotes ?? ''}
                  onChange={(e) => set('additionalNotes', e.target.value)}
                />
              </div>}
              </div>
              <div className="mt-3 d-flex align-items-center justify-content-center gap-3">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={m.isPending}
                >
                  {m.isPending ? 'Creating…' : legacyLayout ? 'Continue' : 'Create'}
                </button>
                {error && <div className="alert alert-danger mb-0 py-2 px-3">{error}</div>}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectInfoIcon({ text }: { text: string }) {
  return (
    <HoverTooltip text={text} placement="left">
      <img
        className="img-fluid"
        src="/assets/images/Icon_Info.png"
        alt={`${text} information`}
        title={text}
      />
    </HoverTooltip>
  );
}

const legacyCreateCopy = {
  title: 'Create New Project',
  subtitle: 'Fill out the fields below to continue.',
  reportNotice: 'This information will be displayed on the final report.',
};

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
